import React, { useEffect, useState } from "react";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, } from "reactstrap";
import { toast } from "react-toastify";
import { addComment, CommentDTO, deleteTask, getTaskById, getTeammember, updateTask } from "../../Api/ApiUrlConst";
import { useAuth } from "../../Context/AuthContext";
import { Save } from "@mui/icons-material";
interface EditTaskComponentsProps {
  taskId: number;
  onClose?: (refresh?: boolean) => void;
}

interface SubTaskUpdateDto {
  id: number;
  description: string;
  assignedToId: string;
  status: string;
  priority: string;
  workHours?: number;
  completionDate?: string | null;
  remarks?: string;
  issues?: string;
  percentCompleted: number; // Corrected type
  SpentHours: number;
}

interface TaskUpdateDto {
  description: string;
  assignedToId: string;
  status: string;
  priority: string;
  percentCompleted: number;
  completionDate?: string | null;
  remarks: string;
  issues: string;
  subTasks: SubTaskUpdateDto[];
  spentHours: number;
}

interface TeamMemberDTO {
  userID: string;
  name: string;
  department: string; // ✅ needed to compare
}


const initialTaskData: TaskUpdateDto = {
  description: "",
  assignedToId: "",
  completionDate: null,
  status: "",
  priority: "",
  percentCompleted: 0,
  remarks: "",
  issues: "",
  subTasks: [],
  spentHours: 0
};

const initialSubTaskData: SubTaskUpdateDto = {
  id: 0,
  description: "",
  assignedToId: "",
  status: "YetToStart",
  priority: "Medium",
  workHours: 0,
  completionDate: null,
  percentCompleted: 0,
  remarks: "",
  issues: "",
  SpentHours: 0
};

const statusOptions = [
  { value: "YetToStart", label: "Yet to Start" },
  { value: "WaitingForInput", label: "Waiting for Input" },
  { value: "", label: "" },
  { value: "ModellingTodo", label: "Modelling ToDo" },
  { value: "ModellingInProgress", label: "Modelling in Progress" },
  { value: "ModellingCompleted", label: "Modelling Completed" },
  { value: "", label: "" },
  { value: "DraftingTodo", label: "Drafting ToDo" },
  { value: "DraftingInProgress", label: "Drafting in Progress" },
  { value: "DraftingCompleted", label: "Drafting Completed" },
  { value: "", label: "" },
  { value: "QCTodo", label: "QC ToDo" },
  { value: "QCInProgress", label: "QC in Progress" },
  { value: "QCCompleted", label: "QC Completed" },
  { value: "", label: "" },
  { value: "ClientReviewTodo", label: "Client Review ToDo" },
  { value: "ClientReviewInProgress", label: "Client Review in Progress" },
  { value: "ClientReviewCompleted", label: "Client Review Completed" },
  { value: "", label: "" },
  { value: "FinalCorrectionTodo", label: "Final Correction ToDo" },
  { value: "FinalCorrectionInProgress", label: "Final Correction in Progress" },
  { value: "FinalCorrectionCompleted", label: "Final Correction Completed" },
  { value: "", label: "" },
  { value: "RevisionTodo", label: "Revision ToDo" },
  { value: "RevisionInProgress", label: "Revision in Progress" },
  { value: "RevisionCompleted", label: "Revision Completed" },
  { value: "", label: "" },
  { value: "Cancelled", label: "Cancelled" },
  { value: 'Issues', label: 'Issues' },
  { value: "OnHold", label: "On Hold" },
  { value: "Released", label: "Released" },
  { value: "NotInScope", label: "Not In Scope" },
   { value: "Completed", label: "Ready for Release" },
];

function EditTaskComponents({ taskId, onClose }: EditTaskComponentsProps) {
  const [task, setTask] = useState<TaskUpdateDto>(initialTaskData);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onHoldModal, setOnHoldModal] = useState(false);
  const [onHoldReason, setOnHoldReason] = useState("");
  const [pendingStatusTask, setPendingStatusTask] = useState<any>(null);
  const [comments, setComments] = useState<CommentDTO[]>([]);
  const { department } = useAuth();


  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const taskRes = await getTaskById(taskId);
        const data = taskRes.data;

        const mappedSubTasks = (data.subTasks || []).map((st: any) => ({
          id: st.id,
          description: st.description,
          assignedToId: st.assignedToId,
          dueDate: st.dueDate ? st.dueDate.slice(0, 10) : null,
          status: st.status,
          priority: st.priority,
          percentCompleted: st.percentCompleted,
          completionDate: st.completionDate
            ? st.completionDate.slice(0, 10)
            : null,
          remarks: st.remarks || "",
          issues: st.issues || "",
          spentHours: st.spentHours ?? 0     // ✅ ADD
        }));


        setTask({
          description: data.description,
          assignedToId: data.assignedToId,
          status: data.status,
          priority: data.priority,
          percentCompleted: data.percentCompleted,
          completionDate: data.completionDate
            ? data.completionDate.slice(0, 10)
            : null,
          remarks: data.remarks || "",
          issues: data.issues || "",
          subTasks: mappedSubTasks,
          spentHours: data.spentHours ?? 0   // ✅ FIX
        });


        const teamRes = await getTeammember();
        setTeamMembers(teamRes.data?.$values || teamRes.data || []);
      } catch (err: any) {
        toast.error(err.message || "Load failed");
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [taskId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // 🔥 Percent Auto Mapping
    const statusPercentMap: Record<string, number> = {
      ModellingCompleted: 30,
      DraftingCompleted: 60,
      QCCompleted: 70,
      ClientReviewCompleted: 80,
      FinalCorrectionCompleted: 90,
      Released: 100,
    };

    setTask(prev => {
      let updated: any = {
        ...prev,
        [name]:
          name === "workHours" ||
            name === "percentCompleted" ||
            name === "SpentHours"
            ? Number(value) || 0
            : value,

      };

      // ------------------------------------------------
      // 🔥 Rule 1: When STATUS changes → Auto set % Done
      // ------------------------------------------------
      if (name === "status" && statusPercentMap[value] !== undefined) {
        updated.percentCompleted = statusPercentMap[value];

        // Auto completion date for final statuses
        if (value === "Released" || value === "Completed") {
          updated.completionDate = new Date().toISOString().slice(0, 10);
        }
      }

      // ------------------------------------------------
      // 🔥 Rule 2: If % entered = 100 → Auto Released
      // ------------------------------------------------
      if (name === "percentCompleted" && Number(value) === 100) {
        updated.status = "Released";
        updated.completionDate = new Date().toISOString().slice(0, 10);
      }

      // ------------------------------------------------
      // 🔥 Rule 3: If manually select Completed/Released → %=100
      // ------------------------------------------------
      if (name === "status") {
        const today = new Date().toISOString().slice(0, 10);

        if (
          value === "Released" ||
          value === "Completed" ||
          value === "All Completed" ||
          value === "Completed & Closed"
        ) {
          updated.percentCompleted = 100;
          updated.completionDate = today;
        }
        else if(value ==="Ready for Release"){
          updated.percentCompleted=98;
        }
      }

      return updated;
    });
  };

  const handleSubTaskChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const updatedSubTasks = [...task.subTasks];
    (updatedSubTasks[index] as any)[name] =
      name === "SpentHours" || name === "percentCompleted"
        ? Number(value) || 0
        : value;

    setTask(prev => ({ ...prev, subTasks: updatedSubTasks }));
  };

  const addSubTask = () => {
    setTask(prev => ({
      ...prev,
      subTasks: [...prev.subTasks, { ...initialSubTaskData }]
    }));
  };

  const removeSubTask = (index: number) => {
    const updatedSubTasks = [...task.subTasks];
    updatedSubTasks.splice(index, 1);
    setTask(prev => ({ ...prev, subTasks: updatedSubTasks }));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()


    try {
      setLoading(true);
      const cleanData = {
        ...task,
        completionDate: task.completionDate || null,
        spentHours: task.spentHours ?? 0,

        subTasks: task.subTasks.map(st => ({
          ...st,
          completionDate: st.completionDate || null,
        }))
      };

      await updateTask(taskId, cleanData);
      toast.success("Task updated successfully");
      onClose?.(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Update failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      setLoading(true);
      await deleteTask(taskId);
      toast.success("Task deleted successfully");
      onClose?.(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Delete failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    task: any
  ) => {

    const value = e.target.value;

    if (value === "OnHold") {

      setPendingStatusTask({
        task,
        status: value
      });

      setOnHoldReason("");
      setOnHoldModal(true);

      return;
    }

    handleChange(e);
  };

  

  const saveOnHoldReason = async () => {

    if (!onHoldReason.trim()) {
      toast.error("Reason is required");
      return;
    }

    try {

      // 1️⃣ SAVE COMMENT API
      const newComment = await addComment({
        entityType: "Task",
        entityId: taskId,
        content: onHoldReason.trim()
      });

      // 2️⃣ ADD COMMENT TO UI STATE
      setComments((prev: any) => [
        newComment,
        ...prev
      ]);

      // 3️⃣ CHANGE STATUS
      const fakeEvent = {
        target: {
          name: "status",
          value: pendingStatusTask.status
        }
      };

      handleChange(fakeEvent as any);

      toast.success("Status updated");

      setOnHoldModal(false);

    } catch (err: any) {

      console.error(err);

      toast.error(
        err?.response?.data?.message ||
        "Failed to save comment"
      );
    }
  };

  const filteredTeamMembers = !department ? teamMembers : teamMembers.filter(m => m.department === department);


  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;



  return (
    <Form onSubmit={handleSubmit}>

      {/* Main Task Row */}

      <tr>
        <td style={{ width: '5%' }}></td>
        <td style={{ width: '25%' }}><Input name="description" value={task.description} onChange={handleChange} required /></td>
        {/* <td style={{ width: '4%' }}><Input
          type="number"
          name="SpentHours"
          value={task.SpentHours}
          onChange={handleChange}
        />
        </td> */}

        <td style={{ width: '4%' }}><Input type="number" name="percentCompleted" min="0" max="100" value={task.percentCompleted}
          //         onChange={(e) => {
          //       const newValue = Number(e.target.value) || 0;
          //       if (newValue < (task.percentCompleted || 0)) return;
          //       handleChange(e);
          //     }} 
          //      onKeyDown={(e) => {
          //   if (e.key === "ArrowDown") e.preventDefault();
          // }}
          onChange={handleChange}
        /></td>

        <td style={{ width: '12%' }}><Input type="select" name="assignedToId" value={task.assignedToId} onChange={handleChange} required>
          <option value="">Select</option>
          {filteredTeamMembers.map(member => (
            <option key={member.userID} value={member.userID}>{member.name}</option>
          ))}
        </Input>
        </td>
        <td style={{ width: '15%' }}>
          <Input
            type="select"
            name="status"
            value={task.status}
            onChange={(e) => handleStatusChange(e, task)}
            required
          >
            <option value="">Select</option>

            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </td>



        <td style={{ width: '12%' }}><Input type="select" name="priority" value={task.priority} onChange={handleChange} required>
          <option value="">Select</option>
          <option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option><option value="Critical">Critical</option>
        </Input></td>



      </tr>

      <div className="d-flex gap-2 justify-content-end mt-3">
        {/* <Button color="secondary" size="sm" onClick={addSubTask}>+</Button> */}
        <Button color="primary" type="submit" disabled={loading}>{loading ? "Updating..." : <Save />}</Button>
        {/* <Button color="danger" onClick={handleDelete} disabled={loading}>{loading ? "Deleting..." : <DeleteOutlineIcon />}</Button> */}
      </div>

      <Modal
        isOpen={onHoldModal}
        toggle={() => setOnHoldModal(false)}
      >
        <ModalHeader toggle={() => setOnHoldModal(false)}>
          On Hold Reason
        </ModalHeader>

        <ModalBody>

          <FormGroup>
            <Label>Reason *</Label>

            <Input
              type="textarea"
              value={onHoldReason}
              onChange={(e) =>
                setOnHoldReason(e.target.value)
              }
              placeholder="Enter reason..."
            />
          </FormGroup>

        </ModalBody>

        <ModalFooter>

          <Button
            color="secondary"
            onClick={() => setOnHoldModal(false)}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={saveOnHoldReason}
          >
            Save
          </Button>

        </ModalFooter>
      </Modal>


    </Form>

  );
}

export default EditTaskComponents;
